import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { useAuth } from "../hooks/use-auth";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { useState, useRef } from "react";
import { Loader2, Image as ImageIcon, AlertCircle, X } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { createPost, uploadMultiplePostMedia, convertImageToJPEG } from "../lib/data";
import { imageErrorMessage } from "../lib/image-prepare";
import { db } from "../lib/firebase";
import { collection, doc } from "@firebase/firestore";
import { isVideoFile, startVideoUpload, validateVideoFile } from "../lib/video-upload";
import { pickNativeMedia } from "../lib/native-media-picker";
import { MediaAttachMenu } from "./media-attach-menu";
import { VideoFileTile } from "./video-file-tile";
import type { Post, User } from "../types";
import { Input } from "./ui/input";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./ui/carousel";

interface CreatePostFormProps {
  onPostCreated: (post: Post & { author: User }) => void;
}

export default function CreatePostForm({ onPostCreated }: CreatePostFormProps) {
  const { user } = useAuth();
  const [content, setContent] = useState("");
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaPreviews, setMediaPreviews] = useState<string[]>([]);
  const [mediaType, setMediaType] = useState<"image" | "video" | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  if (!user) return null;
  const canCreatePost = ["trainer", "admin", "glavni_trener"].includes(
    user.role
  );
  if (!canCreatePost) return null;

  const userInitials = user.name
    .split(" ")
    .map((n) => n[0])
    .join("");

  const resetForm = () => {
    setContent("");
    setMediaFiles([]);
    setMediaPreviews([]);
    setMediaType(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) void handleMediaFiles(Array.from(e.target.files));
  };

  // Gallery in the iOS app (see MediaAttachMenu).
  const pickFromGallery = async () => {
    setError(null);
    try {
      const files = await pickNativeMedia({ limit: 10 }, () => setIsSubmitting(true));
      if (files.length > 0) await handleMediaFiles(files);
    } catch (err) {
      console.error("Gallery pick failed:", err);
      setError("Odabrani medij se nije mogao učitati.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMediaFiles = async (files: File[]) => {
    setError(null);
    if (files.length > 0) {
      setIsSubmitting(true);
      try {
        const firstFileType = isVideoFile(files[0]) ? "video" : "image";

        // Ensure all files are of the same type
        const allSameType = files.every(
          (file) => (isVideoFile(file) ? "video" : "image") === firstFileType
        );

        if (!allSameType) {
          setError(
            "Možete učitati samo slike ili samo videozapise, ne mješavinu."
          );
          return;
        }

        // Enforce video limit
        if (firstFileType === "video") {
          if (files.length > 1) {
            setError("Možete učitati samo jedan videozapis po objavi.");
            return;
          }
          const invalid = await validateVideoFile(files[0]);
          if (invalid) {
            setError(invalid);
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
          }
        }

        let processedFiles = files;
        if (firstFileType === "image") {
          processedFiles = await Promise.all(
            files.map((file) => convertImageToJPEG(file))
          );
        }

        setMediaFiles(processedFiles);
        setMediaType(firstFileType);
        setMediaPreviews(processedFiles.map((file) => URL.createObjectURL(file)));
      } catch (err: any) {
        console.error("Error processing media:", err);
        setError(!isVideoFile(files[0]) ? imageErrorMessage(err) : "Nije uspjelo procesiranje medija.");
        if (fileInputRef.current) fileInputRef.current.value = "";
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const removeMedia = (index: number) => {
    const newFiles = mediaFiles.filter((_, i) => i !== index);
    const newPreviews = mediaPreviews.filter((_, i) => i !== index);

    setMediaFiles(newFiles);
    setMediaPreviews(newPreviews);

    if (newFiles.length === 0) {
      setMediaType(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() && mediaFiles.length === 0) {
      toast({
        file: "components/create-post-form.tsx", code: "NN-VAL-011",
        variant: "destructive",
        title: "Nije moguće kreirati praznu objavu",
        description: "Molimo dodajte sadržaj ili datoteku.",
      });
      return;
    }

    // Videos upload in the background (progress shown in VideoUploadBar);
    // the post is created once the upload finishes.
    if (mediaType === "video" && mediaFiles.length > 0) {
      const postId = doc(collection(db, "posts")).id;
      const postContent = content;
      startVideoUpload({
        label: "Objava",
        file: mediaFiles[0],
        folder: "posts",
        metadata: { refPath: `posts/${postId}` },
        onUploaded: async ({ url, storagePath }) => {
          const newPost = await createPost(user.id, postContent, [url], "video", {
            id: postId,
            extra: { videoProcessing: true, videoProcessingPath: storagePath },
          });
          onPostCreated({ ...newPost, author: user });
          return `posts/${postId}`;
        },
      });
      toast({
        file: "components/create-post-form.tsx", code: "NN-SUC-214",
        title: "Video se prenosi",
        description: "Objava će se pojaviti čim prijenos završi. Ne zatvarajte aplikaciju do tada.",
      });
      resetForm();
      return;
    }

    setIsSubmitting(true);
    setError(null);

    let mediaUrls: string[] = [];

    if (mediaFiles.length > 0 && mediaType) {
      try {
        mediaUrls = await uploadMultiplePostMedia(mediaFiles);
      } catch (uploadError: any) {
        console.error("Failed to upload media:", uploadError);
        setError(
          uploadError.message ||
          "Nije uspjelo učitavanje datoteke. Objava nije kreirana."
        );
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const newPost = await createPost(user.id, content, mediaUrls, mediaType);

      onPostCreated({ ...newPost, author: user });

      toast({
        file: "components/create-post-form.tsx", code: "NN-SUC-012",
        title: "Objava kreirana!",
        description: "Vaša objava je sada vidljiva na feedu.",
      });
      resetForm();
    } catch (postError: any) {
      console.error("Failed to create post:", postError);
      setError(
        postError.message ||
        "Došlo je do neočekivane pogreške prilikom izrade objave."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="mb-6 shadow-md">
      <CardContent className="p-4">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex items-start gap-4">
            <Avatar className="mt-1">
              <AvatarImage src={user.avatar} alt={user.name} />
              <AvatarFallback>{userInitials}</AvatarFallback>
            </Avatar>
            <Textarea
              placeholder={`Što vam je na umu, ${user.name}?`}
              className="flex-1 resize-none border-0 shadow-none focus-visible:ring-0 text-base"
              rows={3}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          {mediaPreviews.length > 0 && (
            <div className="ml-auto w-full max-w-[calc(100%-56px)]">
              <Carousel className="rounded-lg overflow-hidden border">
                <CarouselContent>
                  {mediaPreviews.map((preview, index) => (
                    <CarouselItem key={index}>
                      <div className="relative w-full aspect-video">
                        {mediaType === "image" ? (
                          <img
                            src={preview}
                            alt={`Pregled slike ${index + 1}`}
                            className="object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-black/40 p-4">
                            {mediaFiles[index] && (
                              <VideoFileTile file={mediaFiles[index]} className="w-full max-w-xs" />
                            )}
                          </div>
                        )}
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 h-6 w-6 rounded-full z-10"
                          onClick={() => removeMedia(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                {mediaPreviews.length > 1 && (
                  <>
                    <CarouselPrevious className="absolute left-2" />
                    <CarouselNext className="absolute right-2" />
                  </>
                )}
              </Carousel>
            </div>
          )}

          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Greška</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <MediaAttachMenu
                onGallery={pickFromGallery}
                onOtherFiles={() => fileInputRef.current?.click()}
                disabled={isSubmitting}
              >
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isSubmitting}
                  className="hover:bg-[#e73e7b]"
                >
                  <ImageIcon className="mr-2 h-4 w-4" />
                  Dodaj medij
                </Button>
              </MediaAttachMenu>
              <Input
                id="file-upload"
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={handleMediaChange}
                accept="image/*,video/*"
                disabled={isSubmitting}
                multiple
              />
              {mediaFiles.length > 0 && (
                <span className="text-sm text-muted-foreground truncate max-w-[120px]">
                  {mediaFiles.length} datoteka odabrano
                </span>
              )}
            </div>

            <Button
              type="submit"
              size="sm"
              disabled={
                (!content.trim() && mediaFiles.length === 0) || isSubmitting
              }
              className="bg-accent hover:bg-[#e73e7b] px-4"
            >
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Objavi
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
